import { ChevronDownIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { isRestrictedUser } from '../../../../utils/roles'
import { ListAllActiveUsersResponse } from '../../../interfaces'
import { UserContext } from '../../../providers'

interface MultiSelectMenuProps {
  options: ListAllActiveUsersResponse[]
  selectedUserIds: string[]
  setSelectedUserIds: React.Dispatch<React.SetStateAction<string[]>>
}

const MultiSelectMenu = ({
  options,
  selectedUserIds,
  setSelectedUserIds,
}: MultiSelectMenuProps): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const getButtonLabel = () => {
    if (selectedUserIds.length === 0) return t('created_by', 'Created by')
    if (selectedUserIds.length === 1) {
      const selectedUser = options.find(
        option => option._id === selectedUserIds[0],
      )
      return selectedUser?.userName
    }
    return `${selectedUserIds.length} users selected`
  }

  const handleSelectionChange = (value: string) => {
    const isSelected = selectedUserIds.includes(value)

    if (isSelected) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== value))
    } else {
      setSelectedUserIds([...selectedUserIds, value])
    }
  }

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        variant="outline"
        border="solid 1px"
        borderRadius="base"
        borderColor={selectedUserIds.length ? 'appClearBlue' : 'appPaleLilac'}
        minW="137px"
        minH="35px"
        width="fit-content"
        isDisabled={isRestrictedUser(user?.roles)}
      >
        <Flex pl="17.5px" pr="8px" justify="space-between">
          <Box fontWeight="500" color="appSlate">
            {getButtonLabel()}
          </Box>
          <Box ml="14px">
            <ChevronDownIcon />
          </Box>
        </Flex>
      </MenuButton>
      <MenuList minW="250px" maxH="350px" overflow="auto">
        {options.map(option => (
          <MenuItem
            key={option._id}
            onClick={e => {
              const target = e.target as HTMLInputElement
              if (target.type !== 'checkbox') {
                handleSelectionChange(option._id)
              }
            }}
            closeOnSelect={false}
          >
            <Flex alignItems="center">
              <Checkbox
                isChecked={selectedUserIds.includes(option._id)}
                mr="7px"
              />
              <Avatar size="sm" name={option.userName} mr="7px" />
              <Tooltip label={option.userEmail}>
                <Text isTruncated>
                  {option.userName}{' '}
                  {option._id === user?.id ? t('me_brackets', '(Me)') : null}
                </Text>
              </Tooltip>
            </Flex>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  )
}

export default MultiSelectMenu
